import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Clock, ArrowRight, CheckCircle, Droplets, Wrench, ShoppingBag, Calendar, Truck } from 'lucide-react';
import SEOHead from '../components/SEOHead';

// All zones data
const ZONES = [
    { slug: 'draguignan', name: 'Draguignan', distance: '10 min', postalCode: '83300', population: '40 000' },
    { slug: 'frejus', name: 'Fréjus', distance: '25 min', postalCode: '83600', population: '55 000' },
    { slug: 'saint-raphael', name: 'Saint-Raphaël', distance: '30 min', postalCode: '83700', population: '35 000' },
    { slug: 'sainte-maxime', name: 'Sainte-Maxime', distance: '35 min', postalCode: '83120', population: '14 000' },
    { slug: 'le-muy', name: 'Le Muy', distance: '15 min', postalCode: '83490', population: '9 500' },
    { slug: 'lorgues', name: 'Lorgues', distance: '15 min', postalCode: '83510', population: '9 000' },
    { slug: 'trans-en-provence', name: 'Trans-en-Provence', distance: '12 min', postalCode: '83720', population: '6 000' },
    { slug: 'vidauban', name: 'Vidauban', distance: '20 min', postalCode: '83550', population: '11 000' },
    { slug: 'les-arcs', name: 'Les Arcs-sur-Argens', distance: '18 min', postalCode: '83460', population: '7 500' },
    { slug: 'grimaud', name: 'Grimaud', distance: '40 min', postalCode: '83310', population: '4 500' },
    { slug: 'flayosc', name: 'Flayosc (Siège)', distance: '0 min', postalCode: '83780', population: '4 500', isHQ: true },
];

const SERVICES = [
    { icon: Droplets, title: 'Analyse d\'eau gratuite', desc: 'Test complet pH, chlore, TAC' },
    { icon: ShoppingBag, title: 'Produits professionnels', desc: 'Gamme CTX et O.C.A PRO' },
    { icon: Wrench, title: 'Recherche de fuite', desc: 'Détection et réparation' },
    { icon: Calendar, title: 'Rendez-vous rapide', desc: 'Intervention sous 48h' },
    { icon: Truck, title: 'Livraison express', desc: 'Livraison à domicile' },
];

// Structured data for local business with service area
const getServiceAreaSchema = () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "O.C.A Piscine",
    "description": "Expert en entretien et produits piscine dans le Var (83). Analyse d'eau, traitement, recherche de fuite, équipements.",
    "url": "https://www.ocapiscine.fr",
    "telephone": "+33651961474",
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "5 Place de la République",
        "addressLocality": "Flayosc",
        "postalCode": "83780",
        "addressRegion": "Var",
        "addressCountry": "FR"
    },
    "areaServed": ZONES.map(zone => ({
        "@type": "City",
        "name": zone.name,
        "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Var"
        }
    })),
    "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 43.5308,
            "longitude": 6.4081
        },
        "geoRadius": "50000"
    }
});

export default function ZonesPage() {
    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="zones-page">
            {/* SEO */}
            <SEOHead 
                title="Zones d'intervention Piscine dans le Var (83) | O.C.A Piscine"
                description="O.C.A Piscine intervient dans tout le Var : Draguignan, Fréjus, Saint-Raphaël, Sainte-Maxime, Le Muy, Lorgues... Analyse d'eau, produits, entretien et dépannage piscine."
                keywords="pisciniste Var, piscine Draguignan, piscine Fréjus, piscine Saint-Raphaël, entretien piscine Var, produits piscine 83"
                canonicalUrl="https://www.ocapiscine.fr/zones"
                structuredData={[getServiceAreaSchema()]}
            />

            {/* Hero Section */}
            <section className="relative py-16 bg-gradient-to-br from-primary/10 via-background to-cyan-500/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                            <MapPin className="h-5 w-5" />
                            <span className="font-medium">Intervention dans tout le Var</span>
                        </div>
                        
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                            Nos zones d'intervention
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                            Basés à <strong>Flayosc</strong>, nous intervenons dans tout le <strong>Var (83)</strong> pour l'entretien, 
                            le dépannage et la vente de produits piscine. Intervention rapide sous 48h.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4">
                            <Link to="/appointment">
                                <Button size="lg" className="rounded-full gap-2">
                                    <Calendar className="h-5 w-5" />
                                    Prendre rendez-vous
                                </Button>
                            </Link>
                            <a href="tel:+33651961474">
                                <Button size="lg" variant="outline" className="rounded-full gap-2">
                                    <Phone className="h-5 w-5" />
                                    06 51 96 14 74
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-12 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {SERVICES.map((service, index) => (
                            <div key={index} className="text-center p-4">
                                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                                    <service.icon className="h-6 w-6 text-primary" />
                                </div>
                                <h3 className="font-semibold text-sm mb-1">{service.title}</h3>
                                <p className="text-xs text-muted-foreground">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Zones Grid */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-12">
                        Villes desservies dans le Var
                    </h2>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {ZONES.map((zone) => (
                            <Link key={zone.slug} to={`/zone/${zone.slug}`}>
                                <Card className={`glass rounded-2xl hover:shadow-lg transition-all hover:-translate-y-1 ${zone.isHQ ? 'border-primary border-2' : ''}`}>
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                                                    <MapPin className="h-5 w-5 text-primary" />
                                                    {zone.name}
                                                </h3>
                                                <p className="text-sm text-muted-foreground">{zone.postalCode}</p>
                                            </div>
                                            {zone.isHQ && (
                                                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                                                    Siège
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-1 text-muted-foreground">
                                                <Clock className="h-4 w-4" />
                                                {zone.distance} de Flayosc
                                            </span>
                                            <span className="text-primary flex items-center gap-1">
                                                En savoir plus
                                                <ArrowRight className="h-4 w-4" />
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coverage Map Description */}
            <section className="py-16 bg-muted/30">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                            Pourquoi choisir O.C.A Piscine ?
                        </h2>
                        <p className="text-muted-foreground">
                            Expert local depuis des années, nous connaissons les spécificités des piscines du Var
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            {[
                                'Intervention rapide sous 48h',
                                'Analyse d\'eau gratuite sur place',
                                'Produits professionnels CTX et O.C.A PRO',
                                'Techniciens qualifiés et expérimentés',
                                'Devis gratuit et sans engagement',
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                        
                        <div className="space-y-4">
                            {[
                                'Recherche de fuite par détection acoustique',
                                'Entretien régulier ou ponctuel',
                                'Réparation de pompes et filtration',
                                'Conseils personnalisés pour votre bassin',
                                'Livraison à domicile de produits',
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                        Besoin d'un expert piscine dans le Var ?
                    </h2>
                    <p className="text-lg text-muted-foreground mb-8">
                        Contactez-nous pour un devis gratuit ou prenez rendez-vous en ligne
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/appointment">
                            <Button size="lg" className="rounded-full gap-2">
                                <Calendar className="h-5 w-5" />
                                Prendre rendez-vous
                            </Button>
                        </Link>
                        <Link to="/analysis">
                            <Button size="lg" variant="outline" className="rounded-full gap-2">
                                <Droplets className="h-5 w-5" />
                                Analyser mon eau
                            </Button>
                        </Link>
                        <Link to="/shop">
                            <Button size="lg" variant="outline" className="rounded-full gap-2">
                                <ShoppingBag className="h-5 w-5" />
                                Voir la boutique
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
